import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { Button } from '@/components/ui/Button';
import { adminEmailTemplatesService } from '@/services/admin-email-templates.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { EmailTemplateView } from '@/types/api';

interface TemplateDraft {
  name: string;
  subject: string;
  preheader: string;
  format: 'html' | 'text';
  htmlBody: string;
  textBody: string;
}

const EMPTY: TemplateDraft = {
  name: '',
  subject: '',
  preheader: '',
  format: 'html',
  htmlBody: '',
  textBody: '',
};

function toDraft(t: EmailTemplateView): TemplateDraft {
  return {
    name: t.name,
    subject: t.subject,
    preheader: t.preheader ?? '',
    format: t.format === 'text' ? 'text' : 'html',
    htmlBody: t.htmlBody ?? '',
    textBody: t.textBody ?? '',
  };
}

export function AdminEditEmailTemplatePage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();

  const [draft, setDraft] = useState<TemplateDraft>(EMPTY);
  const [templateId, setTemplateId] = useState<string | null>(isNew ? null : id!);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew || !id) return;
    let cancelled = false;
    setLoading(true);
    adminEmailTemplatesService
      .getById(id)
      .then((t) => {
        if (cancelled) return;
        setDraft(toDraft(t));
        setTemplateId(t.id);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load template.');
        navigate('/admin/subscribers/templates', { replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate]);

  const setField = <K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (saving) return;
    if (!draft.name.trim() || !draft.subject.trim()) {
      toast.error('Name and subject are required.');
      return;
    }
    if (draft.format === 'html' && !draft.htmlBody.trim()) {
      toast.error('HTML templates need an HTML body.');
      return;
    }
    if (draft.format === 'text' && !draft.textBody.trim()) {
      toast.error('Text templates need a text body.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        subject: draft.subject.trim(),
        preheader: draft.preheader.trim() || undefined,
        format: draft.format,
        htmlBody: draft.format === 'html' ? draft.htmlBody : undefined,
        textBody: draft.format === 'text' ? draft.textBody : undefined,
      };
      if (isNew || !templateId) {
        const created = await adminEmailTemplatesService.create(payload);
        toast.success('Template created.');
        navigate(`/admin/subscribers/templates/${created.id}`, { replace: true });
      } else {
        await adminEmailTemplatesService.update(templateId, payload);
        toast.success('Saved.');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateId || isNew) return;
    if (!window.confirm(`Delete "${draft.name || 'this template'}"?`)) return;
    try {
      await adminEmailTemplatesService.remove(templateId);
      toast.success('Deleted.');
      navigate('/admin/subscribers/templates');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (loading) {
    return (
      <AppShell mode="parent">
        <div className="py-16 text-center text-or-slate-500">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/subscribers/templates')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
              aria-label="Back to templates"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </button>
            <div>
              <div className="or-kicker text-or-orange-600">
                {isNew ? 'New template' : 'Edit template'}
              </div>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Template name"
                className="mt-0.5 w-full min-w-[280px] bg-transparent font-display text-[22px] font-bold text-or-slate-900 outline-none placeholder:text-or-slate-400 sm:text-[26px]"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew ? (
              <Button variant="danger" size="md" onClick={handleDelete}>
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </Button>
            ) : null}
            <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4" aria-hidden />
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
          {/* Editor */}
          <div className="flex flex-col gap-4">
            <Field label="Subject">
              <input
                type="text"
                value={draft.subject}
                onChange={(e) => setField('subject', e.target.value)}
                placeholder="Subject line"
                className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
            </Field>
            <Field label="Preheader" hint="The inbox-preview snippet shown after the subject.">
              <input
                type="text"
                value={draft.preheader}
                onChange={(e) => setField('preheader', e.target.value)}
                placeholder="Optional preview text"
                className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
            </Field>
            <Field label="Format">
              <div className="inline-flex rounded-full border border-or-slate-200 bg-or-slate-50 p-1">
                {(['html', 'text'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setField('format', f)}
                    aria-pressed={draft.format === f}
                    className={cn(
                      'rounded-full px-3 py-1 text-[12px] font-semibold uppercase transition-colors',
                      draft.format === f ? 'bg-white text-or-slate-900 shadow' : 'text-or-slate-500',
                    )}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </Field>
            <Field
              label={draft.format === 'html' ? 'HTML body' : 'Text body'}
              hint="Merge fields: {{first_name}}, {{last_name}}, {{name}}, {{email}}. An unsubscribe link + your postal address are appended automatically."
            >
              <textarea
                value={draft.format === 'html' ? draft.htmlBody : draft.textBody}
                onChange={(e) =>
                  setField(draft.format === 'html' ? 'htmlBody' : 'textBody', e.target.value)
                }
                spellCheck={false}
                placeholder={
                  draft.format === 'html' ? '<h1>Hello {{first_name}}</h1>' : 'Hello {{first_name}},'
                }
                className="min-h-[420px] w-full resize-y rounded-xl border border-or-slate-200 bg-white p-4 font-mono text-[13px] leading-relaxed text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
            </Field>
          </div>

          {/* Preview */}
          <aside className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
              Preview
            </div>
            {draft.format === 'html' ? (
              <iframe
                title="Email preview"
                srcDoc={draft.htmlBody || '<p style="color:#94a3b8;font-family:sans-serif">Nothing to preview yet.</p>'}
                sandbox=""
                className="h-[460px] w-full rounded-xl border border-or-slate-200 bg-white"
              />
            ) : (
              <pre className="h-[460px] w-full overflow-auto whitespace-pre-wrap rounded-xl border border-or-slate-200 bg-white p-4 font-mono text-[13px] text-or-slate-800">
                {draft.textBody || 'Nothing to preview yet.'}
              </pre>
            )}
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
        {label}
      </div>
      {children}
      {hint ? <p className="text-[11px] text-or-slate-500">{hint}</p> : null}
    </div>
  );
}
